import React from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Buildings, CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";

/** Enrichissement d'une fiche client via l'annuaire officiel (INSEE Sirene / INPI).
 *  La meilleure correspondance est pré-cochée ; l'utilisateur valide le SIREN. */
export function EnrichDialog({ client, onDone }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const search = async () => {
    setLoading(true);
    setData(null);
    setSelected(null);
    try {
      const r = await api.get(`/clients/${client.id}/enrich-search`);
      setData(r.data);
      setSelected(r.data.suggested_siren || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recherche dans l'annuaire impossible");
      setOpen(false);
    } finally { setLoading(false); }
  };

  const openAndSearch = () => { setOpen(true); search(); };

  const apply = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/enrich`, { siren: selected });
      toast.success("Fiche enrichie depuis l'annuaire officiel");
      setOpen(false);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Enrichissement impossible"); }
    finally { setBusy(false); }
  };

  const markNotFound = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/enrich-status`, { status: "not_found" });
      toast.success("Fiche marquée « non trouvée » — données saisies conservées");
      setOpen(false);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action impossible"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="ghost" title="Enrichir depuis l'annuaire officiel (INSEE/INPI)"
        data-testid={`enrich-btn-${client.id}`} onClick={openAndSearch}>
        <Buildings size={15} className="text-[#2E3192]" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="enrich-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Enrichir — {client.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#64748B]">
            Annuaire officiel des entreprises (INSEE Sirene + INPI). Sélectionnez l'établissement correspondant :
            SIREN, adresse du siège, code APE et dirigeant seront ajoutés à la fiche (sans écraser les champs déjà remplis).
          </p>
          {loading && <div className="text-sm text-zinc-500 py-6 text-center">Recherche en cours…</div>}
          {data && (
            <>
              {data.results.length === 0 ? (
                <div className="text-sm text-zinc-500 py-6 text-center" data-testid="enrich-empty">
                  Aucune correspondance dans l'annuaire pour « {data.client.name} ».
                </div>
              ) : (
                <div className="space-y-2 mt-1">
                  {data.results.map((r) => (
                    <button key={r.siren} type="button" onClick={() => setSelected(r.siren)}
                      data-testid={`enrich-candidate-${r.siren}`}
                      className={`w-full text-left border rounded-md p-3 transition-colors ${selected === r.siren ? "border-[#2E3192] bg-[#F7F5FC]" : "border-[#E2E4F0] bg-white hover:border-[#A99ED6]"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium flex items-center gap-2">
                          {selected === r.siren && <CheckCircle size={15} weight="fill" className="text-[#2E3192]" />}
                          {r.nom}
                          {data.suggested_siren === r.siren && (
                            <span className="text-[10px] uppercase tracking-wider bg-[#E9E4F5] text-[#2E3192] px-1.5 py-0.5 rounded">suggéré</span>
                          )}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${r.etat === "A" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                          {r.etat === "A" ? "Active" : "Cessée"}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 font-mono">SIREN {r.siren}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {[r.adresse, r.ape_libelle && `${r.ape} — ${r.ape_libelle}`, r.dirigeant].filter(Boolean).join(" · ")}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-between gap-2 mt-4">
                <Button variant="outline" onClick={markNotFound} disabled={busy} data-testid="enrich-notfound">
                  Aucune correspondance
                </Button>
                <Button onClick={apply} disabled={!selected || busy} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="enrich-apply">
                  <MagnifyingGlass size={15} className="mr-1" /> {busy ? "Enrichissement…" : "Appliquer à la fiche"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
